import type { Event } from '@/data/types';
import { Calendar, Clock, Users, Target, AlertCircle } from 'lucide-react';

interface EventCalendarProps {
  events: Event[];
}

interface ProcessedEvent {
  date: string;
  parsedDate: Date;
  events: {
    time: string;
    title: string;
    category: 'meeting' | 'interview' | 'orientation' | 'shoot' | 'other';
  }[];
  description?: string;
}

const getEventCategory = (title: string): 'meeting' | 'interview' | 'orientation' | 'shoot' | 'other' => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('interview')) return 'interview';
  if (lowerTitle.includes('meeting')) return 'meeting';
  if (lowerTitle.includes('orientation')) return 'orientation';
  if (lowerTitle.includes('shoot')) return 'shoot';
  return 'other';
};

const getCategoryStyle = (category: string) => {
  switch (category) {
    case 'meeting':
      return 'bg-primary-50 text-primary-700 border-primary-200';
    case 'interview':
      return 'bg-secondary-50 text-secondary-700 border-secondary-200';
    case 'orientation':
      return 'bg-accent-50 text-accent-700 border-accent-200';
    case 'shoot':
      return 'bg-forest-50 text-forest-700 border-forest-200';
    default:
      return 'bg-neutral-100 text-neutral-600 border-neutral-200';
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'meeting':
      return Users;
    case 'interview':
      return AlertCircle;
    case 'orientation':
      return Users;
    case 'shoot':
      return Target;
    default:
      return Calendar;
  }
};

const formatEventDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.getDate(),
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
      parsedDate: date
    };
  } catch {
    return {
      month: '',
      day: '',
      weekday: '',
      parsedDate: new Date()
    };
  }
};

export function EventCalendar({ events }: EventCalendarProps) {
  // Process and group events
  const processedEvents: ProcessedEvent[] = events.map(event => {
    const eventList = [];
    
    if (Array.isArray(event.title)) {
      event.title.forEach((title, idx) => {
        const time = Array.isArray(event.time) ? event.time[idx] || event.time[0] : event.time;
        eventList.push({
          time,
          title,
          category: getEventCategory(title)
        });
      });
    } else {
      eventList.push({
        time: Array.isArray(event.time) ? event.time[0] : event.time,
        title: event.title,
        category: getEventCategory(event.title)
      });
    }

    return {
      date: event.date,
      parsedDate: new Date(event.date),
      events: eventList,
      description: event.description
    };
  });

  // Sort events by date
  processedEvents.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

  return (
    <section>
      <div className="bg-white rounded-lg border border-neutral-200 shadow-soft p-6">
        <h2 className="text-2xl sm:text-3xl font-display font-semibold text-primary-700 mb-6 text-center">
          Upcoming Events
        </h2>
        <p className="text-neutral-600 text-center mb-8">
          Stay up to date with club meetings, orientations, and shooting events
        </p>

        <div className="grid gap-4 sm:gap-6">
        {processedEvents.map((eventDay, dayIndex) => {
          const { month, day, weekday } = formatEventDate(eventDay.date);
          
          return (
            <div key={dayIndex} className="bg-white rounded-lg p-6 shadow-soft border border-neutral-200 hover:shadow-medium transition-shadow">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Date Block */}
                <div className="flex-shrink-0">
                  <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4 text-center min-w-[100px]">
                    <div className="text-xs font-medium text-primary-600 uppercase tracking-wide">
                      {month}
                    </div>
                    <div className="text-2xl font-bold text-primary-700">
                      {day}
                    </div>
                    <div className="text-xs font-medium text-primary-600 uppercase">
                      {weekday}
                    </div>
                  </div>
                </div>

                {/* Events List */}
                <div className="flex-1 space-y-3">
                  {eventDay.events.map((event, eventIndex) => {
                    const categoryStyle = getCategoryStyle(event.category);
                    const CategoryIcon = getCategoryIcon(event.category);
                    
                    return (
                      <div key={eventIndex} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                        {/* Time */}
                        <div className="flex items-center gap-2 text-sm text-neutral-600 font-medium min-w-[80px]">
                          <Clock className="w-4 h-4" />
                          {event.time}
                        </div>

                        {/* Event Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${categoryStyle}`}>
                              <CategoryIcon className="w-3 h-3" />
                              {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                            </span>
                          </div>

                          <h3 className="font-semibold text-neutral-800 leading-tight">
                            {event.title}
                          </h3>
                        </div>
                      </div>
                    );
                  })}

                  {/* Description */}
                  {eventDay.description && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-amber-800 font-medium">
                          {eventDay.description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

        {/* Legend */}
        <div className="mt-8 bg-neutral-100 rounded-lg p-6 shadow-soft border border-neutral-200">
          <h3 className="text-xl font-display font-semibold text-primary-700 mb-4">Event Types</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { category: 'meeting', label: 'General Meeting', icon: Users },
              { category: 'interview', label: 'Member Interview', icon: AlertCircle },
              { category: 'orientation', label: 'Orientation', icon: Users },
              { category: 'shoot', label: 'Shooting Event', icon: Target }
            ].map(({ category, label, icon: Icon }) => (
              <div key={category} className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getCategoryStyle(category)}`}>
                  <Icon className="w-3 h-3" />
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </span>
                <span className="text-xs text-neutral-600 hidden sm:block">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}